import React, { FC } from 'react'
import QRCode from 'qrcode.react'
import { intersection } from 'lodash'
import { Heading, Indent, Text, TextType, UITypes } from 'LibNeo'
import {
	getExternalErrors,
	orderEditSelectors,
	sbolCommonErrors,
	TYPE_SUBMIT_FORM,
} from 'Pages/registratorCabinet'
import { BtnComponent, InputComponent, LinkComponent } from 'LibNeo/components'
import { Col, Form, Row } from 'antd'
import { shallowEqual, useSelector } from 'react-redux'
import { FormNames } from 'Constants/FormNames'
import { FormComponentProps } from 'antd/lib/form'
import { FieldNames } from 'Constants/FieldNames'
import { useFormConnector, useTimer } from 'Src/hooks'
import { SECOND_LIMIT_CONFIRM } from 'Pages/ibro'
import { PhoneInput } from 'LibNeo/containers/form'
import { Callback } from 'Src/types/utilTypes'
import { Validators, VALIDATOR_ERROR_MESSAGE } from 'Modules'
import { onlyDigits } from 'Helpers'
import { ModalContent, ModalContentText } from './styled'

interface ComponentProps extends FormComponentProps {
	submitForm: (type: string, code?: string, need_sms?: boolean, isQrCodeForSbol?: boolean) => void
	close?: Callback
	isQrCodeForSbol: boolean
	need_sms: boolean
}

const CONFIRMATION_CODE_LENGTH = 6

const PhoneConfirmationModal: FC<ComponentProps> = ({
	submitForm,
	close,
	form,
	isQrCodeForSbol,
	need_sms,
}) => {
	const { validateFields, getFieldError, getFieldValue } = form
	const order = useSelector(orderEditSelectors.getOne, shallowEqual)
	useFormConnector(FormNames.REGISTRATOR_CABINET.EMAIL_OR_PHONE_CONFIRMATION, form)

	const { timerSecond } = useTimer(order?.updated_at, SECOND_LIMIT_CONFIRM)

	const seconds = timerSecond % 60

	const externalErrors = getExternalErrors(order.external_errors_array)

	const isViewCodeError = intersection(sbolCommonErrors, externalErrors).length > 0

	const handleSubmit = () => {
		validateFields((errors, values) => {
			submitForm(TYPE_SUBMIT_FORM.SEND_CODE_EMAIL_OR_PHONE, values.code, need_sms)
		})
	}

	return (
		<>
			<Indent bottom={24}>
				<Indent bottom={4}>
					<Heading type="h5">Подтверждение номера</Heading>
				</Indent>
			</Indent>
			{!isQrCodeForSbol && (
				<Indent bottom={16}>
					<Text type={TextType.BODY2}>
						На указанные реквизиты мы отправили код подтверждения. Введите его в соответствующее
						поле.
					</Text>
				</Indent>
			)}
			<Indent bottom={24}>
				<PhoneInput
					label="Номер телефона клиента"
					form={form}
					initialValue={order.phone}
					disabled
				/>
			</Indent>

			{!isViewCodeError && (
				<InputComponent
					fieldOptions={{
						rules: [
							{
								validator: Validators.checkLength({
									payload: {
										min: CONFIRMATION_CODE_LENGTH,
									},
									messageError: {
										[VALIDATOR_ERROR_MESSAGE.MIN]: 'Минимальная длина значения поля — 6 символов',
									},
								}),
							},
						],
						normalize: (value) => onlyDigits(value),
					}}
					label="Код подтверждения"
					form={form}
					fieldName={FieldNames.CODE}
					format="######"
					mask="_"
					placeholder="______"
				/>
			)}

			{isQrCodeForSbol && order?.authentication_link && (
				<Indent top={24}>
					<ModalContent>
						<ModalContentText>
							<Text type={TextType.BODY2}>1. Наведите камеру смартфона на QR-код</Text>
							<Text type={TextType.BODY2}>2. Перейдите по ссылке (она действует 15 минут)</Text>
							<Text type={TextType.BODY2}>3. Подтвердите согласие с условиями Сервиса</Text>
							<Text type={TextType.BODY2}>4. Подтвердите свои данные Сбербанк Онлайн</Text>
							<Text type={TextType.BODY2}>5. Сообщите полученный код партнеру</Text>
						</ModalContentText>
						<QRCode size={140} value={order.authentication_link} />
					</ModalContent>
				</Indent>
			)}

			<Indent top={24}>
				<Row type="flex" gutter={[16, 0]} align="middle" justify="space-between">
					<Col>
						<Row type="flex" gutter={[16, 0]} align="middle" justify="space-between">
							<Col>
								<BtnComponent
									title="Подтвердить номер"
									onClick={handleSubmit}
									disabled={!getFieldValue(FieldNames.CODE) || !!getFieldError(FieldNames.CODE)}
								/>
							</Col>
							<Col>
								<BtnComponent outline title="Не сейчас" onClick={() => close()} />
							</Col>
						</Row>
					</Col>
					{order && (
						<Col>
							{timerSecond > 0 ? (
								<Text type={TextType.CAPTION} colorIndex={5}>
									Отправить код через {(timerSecond - seconds) / 60}:
									{seconds > 9 ? seconds : `0${seconds}`}
								</Text>
							) : (
								<LinkComponent
									type={UITypes.BLACK}
									title={isQrCodeForSbol ? 'Обновить QR-код' : 'Отправить код повторно'}
									onClick={() =>
										submitForm(
											TYPE_SUBMIT_FORM.SEND_CODE_EMAIL_OR_PHONE_AGAIN,
											null,
											need_sms,
											isQrCodeForSbol
										)
									}
								/>
							)}
						</Col>
					)}
				</Row>
			</Indent>
		</>
	)
}

export const PhoneConfirmationModalComponent = Form.create({
	name: FormNames.REGISTRATOR_CABINET.EMAIL_OR_PHONE_CONFIRMATION,
})(PhoneConfirmationModal)
